/**
 * @file Stimulus-контроллер каталога.
 * Оркестрирует взаимодействие между состоянием ({@link CatalogState}), моделью ({@link CatalogModel})
 * и видом ({@link CatalogView}): подписывается на изменения состояния и передаёт снимок виду.
 * Также управляет отображением выбранных фильтров, пагинацией и сортировкой.
 */

import { Controller } from '@hotwired/stimulus'
import CatalogView from '../catalog/catalog_view'
import catalogState from '../stores/catalog_state'
import catalogModel from '../stores/catalog_model'
import { createFilterBtn } from '../utils/dom'
import { disableScroll, enableScroll } from '../utils/global'
import { scrollToElement } from '../utils/anchor_scroll'

/**
 * @class
 * @extends Controller
 */
export default class CatalogController extends Controller {
  static targets = [
    'filtersToggle',
    'filtersCount',
    'selectedFilters',
    'list',
    'item',
    'resultCount',
    'pagination',
    'mapLayout',
    'map',
    'mapToggle',
    'mapToggleLabel',
    'mapExpandToggle',
    'emptyState',
    'hideOnEmpty'
  ]

  static values = {
    perPage: { type: Number, default: 12 },
    resetFilterAriaLabel: { type: String, default: 'Reset filter' }
  }

  #desktopMedia = null
  #mapVisible = false
  #mapExpanded = false
  #scrollLocked = false

  /** Инициализация: парсинг элементов, настройка состояния, создание вида, подписка на состояние. */
  connect() {
    // Синхронизировано с --breakpoint-lg в tailwind.css
    this.#desktopMedia = window.matchMedia('(min-width: 1024px)')
    this.breakpointChanged = this.breakpointChanged.bind(this)
    this.#desktopMedia.addEventListener('change', this.breakpointChanged)

    catalogModel.parseNodesIntoItems(this.itemTargets)
    catalogState.configure({ pageSize: this.perPageValue })
    this.catalogView = new CatalogView({
      root: this.element,
      list: this.hasListTarget ? this.listTarget : null,
      items: this.itemTargets,
      resultCount: this.hasResultCountTarget ? this.resultCountTarget : null,
      pagination: this.hasPaginationTarget ? this.paginationTarget : null,
      emptyState: this.hasEmptyStateTarget ? this.emptyStateTarget : null,
      hideOnEmpty: this.hideOnEmptyTargets
    })

    this.stateChanged = this.stateChanged.bind(this)
    this.unsubscribeFromCatalogState = catalogState.subscribe(this.stateChanged)

    this.stateChanged(catalogState.snapshot)

    const modalFrame = this.#modalFrame()
    if (!modalFrame) return

    this.modalObserver = new MutationObserver(() => this.#syncExpandedState())
    this.modalObserver.observe(modalFrame, { childList: true })
    this.#syncExpandedState()
  }

  /** Отписка от состояния, отключение наблюдателя модального окна, снятие блокировки скролла. */
  disconnect() {
    this.#desktopMedia?.removeEventListener('change', this.breakpointChanged)
    if (this.#scrollLocked) {
      enableScroll()
      this.#scrollLocked = false
    }
    this.modalObserver?.disconnect()
    this.unsubscribeFromCatalogState?.()
  }

  /**
   * Открывает/закрывает панель фильтров через Turbo Stream в модальном окне.
   */
  toggleFilters() {
    const filtersToggle = this.filtersToggleTarget
    const formPath = filtersToggle.dataset.formPath

    if (
      filtersToggle.getAttribute('aria-expanded') === 'true' &&
      this.#modalHasContent()
    ) {
      filtersToggle.setAttribute('aria-expanded', 'false')
      this.#clearModal()
    } else {
      filtersToggle.setAttribute('aria-expanded', 'true')
      this.#loadTurboStream(formPath)
    }
  }

  /**
   * Переключает видимость карты по состоянию свитча.
   * На мобилке карта раскрывается на весь экран с блокировкой скролла страницы.
   * @param {Event} event
   */
  toggleMap(event) {
    this.#setMapVisible(event.currentTarget.checked)
  }

  /** Расширяет/сворачивает карту внутри контейнера страницы, не включая browser fullscreen. */
  toggleMapExpansion() {
    this.#setMapExpanded(!this.#mapExpanded)
  }

  /** Закрывает полноэкранную карту (кнопка «Show list» на мобилке) и сбрасывает свитч. */
  showList() {
    if (this.hasMapToggleTarget) this.mapToggleTarget.checked = false
    this.#setMapVisible(false)
  }

  /** При переходе через lg-брейкпоинт снимает/возвращает блокировку скролла открытой карты. */
  breakpointChanged(event) {
    // На мобилке карта и так занимает экран; сохранять desktop-разворот при
    // смене брейкпоинта нельзя, иначе список останется скрыт после возврата.
    if (!event.matches) this.#setMapExpanded(false)
    this.#syncScrollLock()
  }

  /**
   * Обработчик изменения состояния: передаёт снимок в `CatalogView` и обновляет выбранные фильтры.
   * @param {{ filters: object, sort: string, page: number, pageSize: number }} state
   */
  stateChanged(state) {
    this.filtersData = state.filters
    this.#updateSelectedFilters()

    const snapshot = catalogModel.getSnapshot(state, { now: new Date() })
    this.catalogSnapshot = snapshot
    this.catalogView.render(snapshot, state.sort)

    // При пустом результате свитч карты скрывается — закрываем открытую карту,
    // иначе её нечем будет выключить
    if (snapshot.totalItems === 0) this.showList()
    this.dispatch('filter', {
      detail: { ids: snapshot.orderedIds },
      bubbles: true
    })
  }

  /**
   * Обработчик `data-action` на кнопках сортировки. Достаёт значение `data-sort` и передаёт в состояние.
   * @param {Event} event
   */
  changeSort(event) {
    const option = event.currentTarget.closest('[data-sort]')
    if (!option) return

    catalogState.setSort(option.dataset.sort)
  }

  /**
   * Обработчик кликов по пагинации: определяет нажатую кнопку (назад, вперёд, номер страницы, «показать ещё»)
   * и вызывает соответствующий метод `catalogState`.
   * @param {Event} event
   */
  paginationClick(event) {
    const button = event.target.closest('button')
    if (!button || !this.catalogView.containsPaginationButton(button)) return

    if (button.classList.contains('pagination__show-more')) {
      catalogState.showMore()
      return
    }

    const previousPage = this.catalogSnapshot.page

    if (button.classList.contains('pagination__prev')) {
      catalogState.setPage(previousPage - 1)
    } else if (button.classList.contains('pagination__next')) {
      catalogState.setPage(previousPage + 1)
    } else if (button.dataset.page) {
      catalogState.setPage(Number.parseInt(button.dataset.page, 10))
    }

    // Список рендерится на месте, и на неполной странице документ резко
    // укорачивается — браузер обрезает scroll по новой высоте и вьюпорт
    // «падает» к футеру. Возвращаем пользователя к началу каталога.
    if (this.catalogSnapshot.page !== previousPage) {
      const catalogSection = this.hasListTarget
        ? this.listTarget.closest('section')
        : null

      scrollToElement(catalogSection ?? this.element)
    }
  }

  /**
   * Удаляет отдельное значение фильтра (убирает тег из строки выбранных фильтров).
   * @param {Event} event
   */
  removeFilter(event) {
    const { filterName, filterValue } = event.currentTarget.dataset

    if (this.filtersData[filterName]) {
      this.filtersData[filterName] = this.filtersData[filterName].filter(
        (v) => v.value !== filterValue
      )
    }

    if (this.filtersData[filterName]?.length === 0) {
      delete this.filtersData[filterName]
    }

    catalogState.setFilters(this.filtersData)

    this.#updateSelectedFilters()
  }

  /** Сбрасывает все активные фильтры. */
  resetFilters() {
    this.filtersData = {}
    catalogState.setFilters(this.filtersData)
    this.#updateSelectedFilters()
  }

  /**
   * Показывает/скрывает контейнер карты и синхронизирует блокировку скролла.
   * @param {boolean} visible
   */
  #setMapVisible(visible) {
    if (!this.hasMapTarget) return
    this.#mapVisible = visible
    this.mapTarget.classList.toggle('hidden', !visible)
    this.#syncMapToggleLabel(visible)
    if (!visible) this.#setMapExpanded(false)
    this.#syncScrollLock()
  }

  /**
   * Свитч и открывает, и закрывает карту, поэтому подпись должна называть
   * действие, доступное сейчас: «Показать карту» → «Скрыть карту».
   * @param {boolean} visible
   */
  #syncMapToggleLabel(visible) {
    if (!this.hasMapToggleLabelTarget) return

    const label = this.mapToggleLabelTarget
    const text = visible ? label.dataset.hideLabel : label.dataset.showLabel
    if (text) label.textContent = text
  }

  /**
   * Меняет ширину карты только в сетке каталога и синхронизирует a11y-состояние.
   * @param {boolean} expanded
   */
  #setMapExpanded(expanded) {
    if (!this.hasMapLayoutTarget || !this.hasMapExpandToggleTarget) return

    this.#mapExpanded = expanded
    this.mapLayoutTarget.classList.toggle(
      'catalog-map-layout--expanded',
      expanded
    )

    const toggle = this.mapExpandToggleTarget
    toggle.setAttribute('aria-expanded', String(expanded))
    toggle.setAttribute(
      'aria-label',
      expanded ? toggle.dataset.collapseLabel : toggle.dataset.expandLabel
    )
  }

  /** Скролл страницы блокируется только пока карта открыта на мобилке (fullscreen-оверлей). */
  #syncScrollLock() {
    const shouldLock = this.#mapVisible && !this.#desktopMedia.matches
    if (shouldLock === this.#scrollLocked) return

    this.#scrollLocked = shouldLock
    if (shouldLock) {
      disableScroll()
    } else {
      enableScroll()
    }
  }

  async #loadTurboStream(url) {
    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      if (!response.ok) {
        throw new Error(
          `Failed to load turbo stream: ${response.status} ${response.statusText}`
        )
      }
      const html = await response.text()
      Turbo.renderStreamMessage(html)
    } catch (error) {
      console.error('Ошибка загрузки turbo stream:', error)
    }
  }

  #clearModal() {
    const modalFrame = this.#modalFrame()
    if (modalFrame) modalFrame.innerHTML = ''
  }

  #modalFrame() {
    return document.getElementById('modal')
  }

  #modalHasContent() {
    return this.#modalFrame()?.children.length > 0
  }

  #syncExpandedState() {
    if (!this.hasFiltersToggleTarget) return
    if (!this.#modalHasContent()) {
      this.filtersToggleTarget.setAttribute('aria-expanded', 'false')
    }
  }

  /** Обновляет бейдж со счётчиком активных фильтров на кнопке-переключателе фильтров. */
  #updateFiltersCount() {
    if (!this.hasFiltersCountTarget) return

    const count = Object.values(this.filtersData || {}).reduce(
      (sum, values) => sum + values.length,
      0
    )

    this.filtersCountTarget.textContent = count > 0 ? count : ''
    this.filtersCountTarget.classList.toggle(
      'filter-toggle__badge--hidden',
      count === 0
    )
  }

  #updateSelectedFilters() {
    this.#updateFiltersCount()

    if (!this.hasSelectedFiltersTarget) return

    const selectedFilters = this.selectedFiltersTarget
    const btnsToRemove = selectedFilters.querySelectorAll('.filter-btn')
    btnsToRemove.forEach((btn) => btn.remove())

    Object.entries(this.filtersData).forEach(([name, values]) => {
      values.forEach((value) => {
        const filterBtn = createFilterBtn(value.textValue, false, true, {
          'data-action': 'click->catalog#removeFilter',
          'data-filter-name': name,
          'data-filter-value': value.value,
          'aria-label': this.resetFilterAriaLabelValue
        })
        selectedFilters.appendChild(filterBtn)
      })
    })

    this.selectedFiltersTarget.classList.toggle(
      'hidden',
      // eslint-disable-next-line no-undefined
      this.filtersData === undefined ||
        Object.keys(this.filtersData).length === 0
    )
  }
}
